import { useCallback, useMemo, useState } from 'react';
import { canBulkProvisionFacility } from './utils/provisioning-jobs.utils';

const normalizeKey = (key) => String(key);

/** Rows on the current page that can be bulk-provisioned. */
export const getEligiblePageRows = (pageRows = []) =>
  pageRows
    .map((row) => row.rowData ?? row)
    .filter((row) => canBulkProvisionFacility(row));

/**
 * Parent-controlled checkbox selection for the Facility LMS grid. Selection
 * survives pagination — `selectedRows` is resolved from the full facility list.
 */
export default function useFacilityBulkSelection(allRows = []) {
  const [selectedIds, setSelectedIds] = useState(() => new Set());

  const toggleRow = useCallback((locationKey) => {
    const id = normalizeKey(locationKey);
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const selectAllEligibleOnPage = useCallback((pageRows = []) => {
    const eligibleIds = getEligiblePageRows(pageRows).map((row) => normalizeKey(row.locationKey));
    setSelectedIds((prev) => {
      const next = new Set(prev);
      eligibleIds.forEach((id) => next.add(id));
      return next;
    });
  }, []);

  const toggleSelectAllOnPage = useCallback((pageRows = []) => {
    const eligible = getEligiblePageRows(pageRows);
    if (eligible.length === 0) return;
    const eligibleIds = eligible.map((row) => normalizeKey(row.locationKey));
    setSelectedIds((prev) => {
      const allSelected = eligibleIds.every((id) => prev.has(id));
      const next = new Set(prev);
      if (allSelected) {
        eligibleIds.forEach((id) => next.delete(id));
      } else {
        eligibleIds.forEach((id) => next.add(id));
      }
      return next;
    });
  }, []);

  const selectedCount = selectedIds.size;

  const selectedRows = useMemo(
    () => allRows.filter((row) => selectedIds.has(normalizeKey(row.locationKey))),
    [allRows, selectedIds]
  );

  const selectedFacilityIds = useMemo(
    () => [...selectedIds].map((id) => Number(id)).filter((id) => Number.isFinite(id)),
    [selectedIds]
  );

  const getPageSelectionState = useCallback(
    (pageRows = []) => {
      const eligible = getEligiblePageRows(pageRows);
      const eligibleOnPageCount = eligible.length;
      if (eligibleOnPageCount === 0) {
        return {
          eligibleOnPageCount: 0,
          headerChecked: false,
          headerIndeterminate: false,
          headerDisabled: true,
        };
      }
      const eligibleIds = eligible.map((row) => normalizeKey(row.locationKey));
      const selectedOnPage = eligibleIds.filter((id) => selectedIds.has(id)).length;
      const allEligibleSelected = selectedOnPage === eligibleOnPageCount;
      const someEligibleSelected = selectedOnPage > 0 && !allEligibleSelected;
      return {
        eligibleOnPageCount,
        headerChecked: allEligibleSelected,
        headerIndeterminate: someEligibleSelected,
        headerDisabled: false,
      };
    },
    [selectedIds]
  );

  return {
    selectedIds,
    selectedCount,
    selectedRows,
    selectedFacilityIds,
    toggleRow,
    clearSelection,
    selectAllEligibleOnPage,
    toggleSelectAllOnPage,
    getPageSelectionState,
  };
}
